// ============================================================================
// INSPIRATION WALL WIDGET
// Animated floating cards showing news, social quotes, and persona perspectives.
// Cards drift slowly around the screen, bouncing gently off edges.
// Position updates use direct DOM manipulation (transform) to avoid React re-renders.
// ============================================================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Newspaper, MessageCircle, UserCircle, Link2, Heart, Eye, Podcast,
} from 'lucide-react';
import { useSecondScreenData } from '../../../contexts/SecondScreenContext';
import type { WidgetProps } from '../../../types/second-screen.types';
import type { InspirationCard, InspirationCardSource } from '../../../types/inspiration-wall.types';

// Source badge colours
const SOURCE_STYLES: Record<InspirationCardSource, { bg: string; text: string; label: string }> = {
  twitter:  { bg: 'bg-blue-500/20',   text: 'text-blue-400',   label: 'X' },
  linkedin: { bg: 'bg-indigo-500/20', text: 'text-indigo-400', label: 'LinkedIn' },
  reddit:   { bg: 'bg-orange-500/20', text: 'text-orange-400', label: 'Reddit' },
  podcast:  { bg: 'bg-purple-500/20', text: 'text-purple-400', label: 'Podcast' },
  news:     { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'News' },
  blog:     { bg: 'bg-amber-500/20',  text: 'text-amber-400',  label: 'Blog' },
  persona:  { bg: 'bg-primary/20',    text: 'text-primary',    label: 'Persona' },
};

const SOURCE_ICONS: Record<string, React.ReactNode> = {
  twitter:  <MessageCircle size={10} />,
  linkedin: <MessageCircle size={10} />,
  reddit:   <MessageCircle size={10} />,
  podcast:  <Podcast size={10} />,
  news:     <Newspaper size={10} />,
  blog:     <Newspaper size={10} />,
  persona:  <UserCircle size={10} />,
};

function formatMetric(n?: number): string {
  if (!n) return '';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

// Card size in pixels
const CARD_W = 330;
const CARD_H = 194;
const CARD_PAD = 12;

// Drift speed in pixels per second
const DRIFT_SPEED_MIN = 8;
const DRIFT_SPEED_MAX = 18;

// Seeded RNG helper
function makeRng(seed: number) {
  let s = seed;
  return () => {
    s = (s * 16807 + 0) % 2147483647;
    return (s % 10000) / 10000;
  };
}

// Generate well-spaced random positions
function generatePositions(
  count: number,
  containerW: number,
  containerH: number,
  seed: number,
): Array<{ left: number; top: number }> {
  const positions: Array<{ left: number; top: number }> = [];
  const maxLeft = Math.max(0, containerW - CARD_W - CARD_PAD);
  const maxTop = Math.max(0, containerH - CARD_H - CARD_PAD);
  const random = makeRng(seed);

  for (let i = 0; i < count; i++) {
    let best = { left: CARD_PAD, top: CARD_PAD };
    let bestMinDist = -1;

    for (let attempt = 0; attempt < 30; attempt++) {
      const left = CARD_PAD + random() * maxLeft;
      const top = CARD_PAD + random() * maxTop;

      let minDist = Infinity;
      for (const p of positions) {
        const dx = left - p.left;
        const dy = top - p.top;
        minDist = Math.min(minDist, Math.sqrt(dx * dx + dy * dy));
      }

      if (minDist > bestMinDist) {
        bestMinDist = minDist;
        best = { left, top };
      }
    }

    positions.push(best);
  }

  return positions;
}

function randomVelocity(random: () => number): { vx: number; vy: number } {
  const angle = random() * Math.PI * 2;
  const speed = DRIFT_SPEED_MIN + random() * (DRIFT_SPEED_MAX - DRIFT_SPEED_MIN);
  return { vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed };
}

// Mutable drift state — lives outside React to avoid re-renders
interface DriftEntry {
  x: number;
  y: number;
  vx: number;
  vy: number;
  el: HTMLDivElement | null;
}

interface VisibleCard {
  card: InspirationCard;
  state: 'entering' | 'visible' | 'exiting';
  initialPosition: { left: number; top: number };
  slotIndex: number; // stable index into driftState array
  rippleDelay: number; // seconds — offsets the ripple animation so cards don't move in sync
}

const InspirationWallWidget: React.FC<WidgetProps> = ({ config }) => {
  const { state, theme } = useSecondScreenData();
  const isDark = theme === 'dark';
  const containerRef = useRef<HTMLDivElement>(null);

  const rotationInterval = parseInt(config?.rotationInterval || '15', 10) * 1000;
  const maxVisible = parseInt(config?.maxVisibleCards || '8', 10);

  // Stabilize cards reference — only update when card IDs actually change
  const rawCards = state?.inspirationCards ?? [];
  const cardsSignatureRef = useRef('');
  const stableCardsRef = useRef<InspirationCard[]>([]);

  const newSignature = rawCards.map((c: any) => c.id).join(',');
  if (newSignature !== cardsSignatureRef.current) {
    cardsSignatureRef.current = newSignature;
    stableCardsRef.current = rawCards;
  }
  const cards = stableCardsRef.current;

  const [visibleCards, setVisibleCards] = useState<VisibleCard[]>([]);
  const poolIndexRef = useRef(0);
  const rotationTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const positionSeedRef = useRef(1);

  // Mutable drift state — updated in rAF, never triggers React renders
  const driftStateRef = useRef<DriftEntry[]>([]);
  const rafRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(0);

  const getContainerSize = useCallback(() => {
    if (!containerRef.current) return { w: 800, h: 500 };
    return {
      w: containerRef.current.clientWidth,
      h: containerRef.current.clientHeight,
    };
  }, []);

  // Initialize visible cards when cards data changes
  useEffect(() => {
    if (cards.length === 0) {
      setVisibleCards([]);
      driftStateRef.current = [];
      poolIndexRef.current = 0;
      return;
    }

    const { w, h } = getContainerSize();
    const count = Math.min(maxVisible, cards.length);
    const positions = generatePositions(count, w, h, 42);
    const rng = makeRng(123);

    // Build drift state
    const driftEntries: DriftEntry[] = [];
    const initial: VisibleCard[] = cards.slice(0, count).map((card, i) => {
      const vel = randomVelocity(rng);
      driftEntries.push({
        x: positions[i].left,
        y: positions[i].top,
        vx: vel.vx,
        vy: vel.vy,
        el: null,
      });
      return {
        card,
        state: 'entering' as const,
        initialPosition: positions[i],
        slotIndex: i,
        rippleDelay: rng() * -6, // stagger ripple phase per card
      };
    });

    driftStateRef.current = driftEntries;
    setVisibleCards(initial);
    poolIndexRef.current = count;

    const timer = setTimeout(() => {
      setVisibleCards(prev => prev.map(vc => ({ ...vc, state: 'visible' as const })));
    }, 600);

    return () => clearTimeout(timer);
  }, [cards, maxVisible, getContainerSize]);

  // rAF drift loop — updates DOM transforms directly, no React state
  useEffect(() => {
    const animate = (time: number) => {
      if (!lastTimeRef.current) lastTimeRef.current = time;
      const dt = Math.min((time - lastTimeRef.current) / 1000, 0.1);
      lastTimeRef.current = time;

      const { w, h } = getContainerSize();
      const maxLeft = Math.max(0, w - CARD_W - CARD_PAD);
      const maxTop = Math.max(0, h - CARD_H - CARD_PAD);

      const entries = driftStateRef.current;
      const collisionW = CARD_W + CARD_PAD;
      const collisionH = CARD_H + CARD_PAD;

      for (const d of entries) {
        if (!d.el) continue;

        d.x += d.vx * dt;
        d.y += d.vy * dt;

        // Wall bounce
        if (d.x < CARD_PAD) { d.x = CARD_PAD; d.vx = Math.abs(d.vx); }
        if (d.x > maxLeft)  { d.x = maxLeft;  d.vx = -Math.abs(d.vx); }
        if (d.y < CARD_PAD) { d.y = CARD_PAD; d.vy = Math.abs(d.vy); }
        if (d.y > maxTop)   { d.y = maxTop;   d.vy = -Math.abs(d.vy); }
      }

      // AABB collision detection — push overlapping cards apart
      for (let i = 0; i < entries.length; i++) {
        const a = entries[i];
        if (!a.el) continue;
        for (let j = i + 1; j < entries.length; j++) {
          const b = entries[j];
          if (!b.el) continue;

          const overlapX = collisionW - Math.abs(a.x - b.x);
          const overlapY = collisionH - Math.abs(a.y - b.y);

          if (overlapX > 0 && overlapY > 0) {
            // Resolve along the axis with smallest overlap
            if (overlapX < overlapY) {
              const push = overlapX * 0.5;
              if (a.x < b.x) { a.x -= push; b.x += push; }
              else            { a.x += push; b.x -= push; }
              // Swap x-velocity direction
              if (Math.sign(a.vx) === Math.sign(b.vx)) {
                a.vx = -a.vx;
              } else {
                const tmp = a.vx; a.vx = b.vx; b.vx = tmp;
              }
            } else {
              const push = overlapY * 0.5;
              if (a.y < b.y) { a.y -= push; b.y += push; }
              else            { a.y += push; b.y -= push; }
              if (Math.sign(a.vy) === Math.sign(b.vy)) {
                a.vy = -a.vy;
              } else {
                const tmp = a.vy; a.vy = b.vy; b.vy = tmp;
              }
            }

            // Re-clamp after collision push to keep cards in bounds
            a.x = Math.max(CARD_PAD, Math.min(a.x, maxLeft));
            a.y = Math.max(CARD_PAD, Math.min(a.y, maxTop));
            b.x = Math.max(CARD_PAD, Math.min(b.x, maxLeft));
            b.y = Math.max(CARD_PAD, Math.min(b.y, maxTop));
          }
        }
      }

      // Apply transforms
      for (const d of entries) {
        if (!d.el) continue;
        d.el.style.transform = `translate(${d.x}px, ${d.y}px)`;
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [getContainerSize]);

  // Rotation: swap one card at a time
  const rotateCard = useCallback(() => {
    if (cards.length <= maxVisible) return;

    setVisibleCards(prev => {
      if (prev.length === 0) return prev;
      const exitIndex = Math.floor(Math.random() * prev.length);
      return prev.map((vc, i) =>
        i === exitIndex ? { ...vc, state: 'exiting' as const } : vc
      );
    });

    setTimeout(() => {
      setVisibleCards(prev => {
        const exitingIndex = prev.findIndex(vc => vc.state === 'exiting');
        if (exitingIndex < 0) return prev;

        const nextIndex = poolIndexRef.current % cards.length;
        poolIndexRef.current = nextIndex + 1;

        const { w, h } = getContainerSize();
        positionSeedRef.current += 1;
        const newPositions = generatePositions(1, w, h, positionSeedRef.current * 7919);
        const rng = makeRng(positionSeedRef.current * 3571);
        const vel = randomVelocity(rng);

        const slotIndex = prev[exitingIndex].slotIndex;

        // Update drift state for this slot
        if (driftStateRef.current[slotIndex]) {
          driftStateRef.current[slotIndex].x = newPositions[0].left;
          driftStateRef.current[slotIndex].y = newPositions[0].top;
          driftStateRef.current[slotIndex].vx = vel.vx;
          driftStateRef.current[slotIndex].vy = vel.vy;
          driftStateRef.current[slotIndex].el = null; // will be re-assigned via ref
        }

        const newCard: VisibleCard = {
          card: cards[nextIndex],
          state: 'entering',
          initialPosition: newPositions[0],
          slotIndex,
          rippleDelay: Math.random() * -6,
        };

        const updated = [...prev];
        updated[exitingIndex] = newCard;
        return updated;
      });

      setTimeout(() => {
        setVisibleCards(prev =>
          prev.map(vc => vc.state === 'entering' ? { ...vc, state: 'visible' } : vc)
        );
      }, 600);
    }, 400);
  }, [cards, maxVisible, getContainerSize]);

  // Set up rotation interval
  useEffect(() => {
    if (cards.length <= maxVisible) return;

    rotationTimerRef.current = setInterval(rotateCard, rotationInterval);
    return () => {
      if (rotationTimerRef.current) clearInterval(rotationTimerRef.current);
    };
  }, [rotateCard, rotationInterval, cards.length, maxVisible]);

  // Empty state
  if (cards.length === 0) {
    return (
      <div ref={containerRef} className={`flex flex-col items-center justify-center h-full gap-3 ${
        isDark ? 'text-white/30' : 'text-slate-400'
      }`}>
        <Newspaper size={32} className="animate-pulse" />
        <p className="text-sm font-medium">Waiting for Social Pulse...</p>
        <p className="text-xs opacity-60">Run the Social Pulse task to populate</p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative w-full h-full overflow-hidden">
      {visibleCards.map((vc) => (
        <CardItem
          key={`${vc.card.id}-${vc.slotIndex}`}
          card={vc.card}
          animState={vc.state}
          isDark={isDark}
          rippleDelay={vc.rippleDelay}
          driftRef={(el) => {
            if (el && driftStateRef.current[vc.slotIndex]) {
              const d = driftStateRef.current[vc.slotIndex];
              d.el = el;
              // Immediately apply current position to prevent flash at (0,0)
              el.style.transform = `translate(${d.x}px, ${d.y}px)`;
            }
          }}
        />
      ))}
    </div>
  );
};

// Individual card — positioned via transform from the drift loop
const CardItem: React.FC<{
  card: InspirationCard;
  animState: 'entering' | 'visible' | 'exiting';
  isDark: boolean;
  rippleDelay: number;
  driftRef: (el: HTMLDivElement | null) => void;
}> = ({ card, animState, isDark, rippleDelay, driftRef }) => {
  const sourceStyle = SOURCE_STYLES[card.source] || SOURCE_STYLES.news;

  const animClass =
    animState === 'entering' ? 'animate-card-enter' :
    animState === 'exiting' ? 'animate-card-exit' :
    '';

  return (
    <div
      ref={driftRef}
      className={`
        absolute top-0 left-0 rounded-lg p-3 flex flex-col gap-1.5 shadow-md overflow-hidden
        ${isDark
          ? 'bg-white/[0.07] border border-white/10 backdrop-blur-sm'
          : 'bg-white border border-slate-200 shadow-slate-200/50'
        }
        ${animClass}
      `}
      style={{
        width: CARD_W,
        height: CARD_H,
        willChange: 'transform',
        // Water ripple: gentle rotation + vertical bob, staggered per card
        animation: animState === 'visible' ? `water-ripple 6s ease-in-out ${rippleDelay}s infinite` : undefined,
      }}
    >
      {/* Header: avatar + name + source badge */}
      <div className="flex items-center gap-2">
        <div className={`
          w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0
          ${card.type === 'persona'
            ? 'bg-primary/20 text-primary'
            : card.type === 'news'
              ? isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-600'
              : isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600'
          }
        `}>
          {card.authorAvatarUrl ? (
            <img
              src={card.authorAvatarUrl}
              alt={card.authorName}
              className="w-7 h-7 rounded-full object-cover"
            />
          ) : (
            SOURCE_ICONS[card.source] || <MessageCircle size={12} />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className={`text-base font-semibold truncate leading-tight ${
            isDark ? 'text-white' : 'text-slate-900'
          }`}>
            {card.authorName}
          </div>
          {card.authorHandle && (
            <div className={`text-sm truncate leading-tight ${
              isDark ? 'text-white/40' : 'text-slate-400'
            }`}>
              {card.authorHandle}
            </div>
          )}
        </div>

        <span className={`
          inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-medium flex-shrink-0
          ${sourceStyle.bg} ${sourceStyle.text}
        `}>
          {SOURCE_ICONS[card.source]}
          {card.sourceLabel || sourceStyle.label}
        </span>
      </div>

      {/* Quote — clamped to 3 lines */}
      <p className={`text-sm leading-snug line-clamp-3 flex-1 ${
        isDark ? 'text-white/80' : 'text-slate-700'
      }`}>
        &ldquo;{card.quote}&rdquo;
      </p>

      {/* Footer: metrics + link agent */}
      <div className="flex items-center justify-between mt-auto">
        <div className={`flex items-center gap-2 text-xs ${
          isDark ? 'text-white/30' : 'text-slate-400'
        }`}>
          {card.metrics?.views != null && (
            <span className="flex items-center gap-0.5">
              <Eye size={10} />
              {formatMetric(card.metrics.views)}
            </span>
          )}
          {card.metrics?.likes != null && (
            <span className="flex items-center gap-0.5">
              <Heart size={10} />
              {formatMetric(card.metrics.likes)}
            </span>
          )}
        </div>

        {card.linkAgent && (
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-medium bg-primary/20 text-primary">
            <Link2 size={10} />
            Link Agent
          </span>
        )}
      </div>
    </div>
  );
};

export default InspirationWallWidget;
